/* eslint-disable no-undefined */
import React, {useState, useEffect, useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {TscStyles, TscThemeName, TscThemeNames, Spinner, TscButtonType, SelectInput, EmptyState} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import Constants, {AnnotationModes, ServiceNames} from '../../constants/Constants';
import config from '../../service/config';
import Comment from './Comment';
import CommentInput from './CommentInput';
import {IComment} from '../../model/commentModel';
import CommentFilter from './CommentFilter';
import {useTranslation} from 'react-i18next';
import UserMessaging from '../../util/UserMessaging';
import {IWorkerState, SetupWorkerState} from '../../model/workerState';
import SelfIdentifyModal from '../modals/SelfIdentifyModal';
import selfIdentifyService from '../../service/selfIdentifyservice';
import {selfIdentifiedUserStore, updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import {ButtonStyledAsAnchor} from '../util/StyledElements';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {canvasStore} from '../../context/canvasProvider';
import {commentsStore, updateCommentsState} from '../../context/commentsProvider';
import {sortCommentsByTime} from '../../util/CommentSorter';
import {conversationsStore, updateConversationsState} from '../../context/conversationsProvider';
import {IConversationData} from '../../model/conversationModel';
import CreateConversationModal from '../modals/CreateConversationModal';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import LocalStorageWrapper from '../util/LocalStorageWrapper';
import AriaHelper from '../../util/AriaHelper';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import {mediaOperationsStore} from '../../context/mediaOperationsProvider';
import logging from '../../service/logging';
import conversationApi from '../../service/conversationApi';
import {AlertModalPortal} from '../util/ModalPortalHelper';
import {importMediaInterface} from '../../hooks/dynamicImport';
import CssUtil from '../../util/CssUtil';
import ConversationEmptyState from '../tabs/conversations/ConversationEmptyState';
import {updateMediaTabsState} from '../../context/mediaTabsProvider';

const ConversationContainer = styled.div<{theme: TscThemeNames; isLti: boolean; smartPlayerDisplayMode: string}>`
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   display: flex;
   flex-direction: column;
   flex: 0 0 auto;
   width: 'auto';
   height: inherit;
   z-index: ${cssConstants.zIndex.conversation};
   outline: none;
   position: relative;

   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px) {
      width: 100%;
      height: auto;
      top: 0;
      right: 0;
      margin-top: 0;
      position: relative;
      overflow: visible;
      border-radius: 0;
      height: 100%;

      /* Prevent iOS mobile auto zoom on textarea focus */
      @supports (-webkit-overflow-scrolling: touch) {
         textarea {
            font-size: 1rem;
         }
      }
   }
`;

const CommentSection = styled.ol<{showEmptyState: boolean}>`
   position: relative;
   padding: 0.5rem 0 0rem 0;
   height: 100%;
   -webkit-overflow-scrolling: touch;
   list-style: none;
   margin: ${props => props.showEmptyState ? 'auto' : '0'};
   overflow: ${props => props.showEmptyState ? 'hidden' : 'visible'};
`;

const CommentSectionHeader = styled.div<{theme: TscThemeNames}>`
   margin-top: 12px;
   display: flex;
   padding: 0 0 12px;
   font-size: 1rem;
   font-weight: 600;
   align-items: center;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   flex: 0 0 auto;
`;

const CommentHeaderRight = styled.div`
   display: flex;
   flex-grow: 1;
   justify-content: flex-end;
`;

const SpinnerWrapper = styled.div`
   height: calc(100vh - 5.25rem);
   display: flex;
   align-items: center;
   padding: 5rem;
`;

const AuthorNameSpan = styled.span`
   font-weight: 600;
`;

export const EmptyStateWrapper = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   height: 100%;
   font-size: .875rem;

   > div {
      box-sizing: border-box;
   }
`;

const SelfIdentifiedUserWrapper = styled.div<{theme: TscThemeNames}>`
   padding: 0.5rem 1rem 0;
   display: flex;
   font-weight: 400;
   word-break: break-word;
   align-items: center;
   justify-content: space-between;
   font-size: 0.875rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};

   > * {
      margin-left: 0.5rem;
      flex-shrink: 0;
   }
`;

const ConversationSelectWrapper = styled.div<{theme: TscThemeNames; borderNeeded: boolean}>`
   padding: 0.5rem 1rem;
   border-bottom: 1px solid ${props => !props.borderNeeded ? 'transparent' : props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};

   label {
      position: absolute;
      left: -9999px;
   }

   /* Prevent iOS mobile auto zoom on textarea focus */
   @supports (-webkit-overflow-scrolling: touch) {
      select {
         font-size: 1rem;
      }
   }
`;

const NoCommentEmptyStateMessageText = styled.div`
`;

const BottomSpacer = styled.div`
   height: 1px; // Prevents grid from collapsing bottom conversation pane margin
`;

const headerBackgroundOpacity = 0.75;
const ConversationContainerHeaderWrapper = styled.div<{theme: TscThemeNames}>`
   position: sticky;
   top: 0;
   padding: 0 12px 8px;
   background-color: ${props => CssUtil.toRgba(props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light, headerBackgroundOpacity)};
   backdrop-filter: blur(12px);
   z-index: ${cssConstants.zIndex.viewPageElement};
   box-shadow: ${props => props.theme === TscThemeName.dusk ? '0 1px 5px rgba(0,0,0,0.2)' : '0 1px 5px rgba(0,0,0,0.08)'};

   :after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      backdrop-filter: ${props => props.theme === TscThemeName.dusk ? 'brightness(110%)' : 'brightness(200%)'};
   }

   :empty {
      display: none;
   }

   :empty:after {
      display: none;
   }
`;

const EmptyStateContainer = styled.div`
   display: flex;
   flex-direction: column;
   justify-content: center;
   align-items: center;
   padding: 1rem;
   width: 100%;
   text-align: center;
   max-width: 24rem;
   margin-bottom: 8rem;
`;

const TitleContainer = styled.div<{theme: TscThemeNames}>`
   font-weight: 600;
   font-size: 1.25rem;
   margin-bottom: .5rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   padding-top: 1rem;
`;

export const ConversationBase: React.FC<IStateMappedProps & IConversationProps> = ({canvasRef, annotationMode, comments, conversations, Title, Author, VideoReviewId, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, Hash, showSelfIdentifyModal, IsReplacingMedia}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [addCommentLoadingState, setAddCommentLoadingState] = SetupWorkerState();
   const [searchText, updateSearchText] = useState('');
   const [isHidingResolvedComments, setIsHidingResolvedComments] = useState(true);
   const [cachedNewComments, setCachedNewComments] = useState<IComment[]>([]);
   const [commentsToHighlight, updateCommentsToHighlight] = useState(null);
   const [showSelfIdentifyAlertModal, setShowSelfIdentifyAlertModal] = useState(false);
   const [showCreateConversationModal, setShowCreateConversationModal] = useState(false);
   const isSelfIdentified = config.user.TechSmithId !== Constants.nullTechSmithId && config.user.DisplayName && !config.user.IsSignedIn;
   const commentsRef = React.useRef<IComment[]>([]);
   commentsRef.current = comments || [];
   const pollIdRef = React.useRef<number>(-1);
   const lastUpdateRef = React.useRef<number>(Date.now());

   const refreshComments = useCallback(async () => {
      const newComments = await conversationApi.getConversationComments(VideoReviewId);

      if (JSON.stringify(mapCommentsToTextArray(newComments)) !== JSON.stringify(mapCommentsToTextArray(commentsRef.current))) {
         if (commentsRef.current.some(c => c.isFiltered)) {
            setCachedNewComments(newComments);
         } else {
            setCommentsToHighlight(commentsRef.current, newComments);
            updateCommentsState({comments: newComments});
         }
      }

      lastUpdateRef.current = Date.now();
   }, [VideoReviewId]);

   useEffect(() => {
      config.user.TechSmithId === Constants.nullTechSmithId && selfIdentifyService.setupSelfIdentifiedUserFromLocalStorage();

      const visibilityChanged = async (): Promise<void> => {
         if (document.visibilityState === 'visible' && lastUpdateRef.current + Constants.conversationRefreshInterval < Date.now()) {
            await refreshComments();
            lastUpdateRef.current = Date.now();
         }
      };

      document.addEventListener('visibilitychange', visibilityChanged);

      return () => {
         document.removeEventListener('visibilitychange', visibilityChanged);
      };
   }, [refreshComments]);

   useEffect(() => {
      if (config.featureSwitches.UseSingleConversation && conversations?.length) {
         const singleConversation = conversations[0];
         updateSelectedConversationState({Title: singleConversation.Title, VideoReviewId: singleConversation.VideoReviewId, ConversationHash: singleConversation.ConversationHash});
      }
   }, [conversations]);

   useEffect(() => {
      const startPoll = () => {
         window.clearInterval(pollIdRef.current);

         pollIdRef.current = window.setInterval(() => {
            if (!IsReplacingMedia) {
               const asyncIntervalFunc = async () => {
                  try {
                     if (document.visibilityState === 'visible') {
                        await refreshComments();
                     }
                  } catch (e) {
                     if (e?.status === Constants.statusCodes.notFound) {
                        window.clearInterval(pollIdRef.current);
                        logging.warn('Detected that review was deleted. Stopping comment polling loop.');
                        UserMessaging.warn(t('conversations.conversationDeleted'));
                     }
                  }
               };

               void asyncIntervalFunc(); // intentional non-await-async in interval
            }
         }, Constants.conversationRefreshInterval);
      };

      VideoReviewId && startPoll();

      return () => {
         window.clearInterval(pollIdRef.current);
      };
   }, [VideoReviewId, IsReplacingMedia, refreshComments, t]);

   useEffect(() => {
      initialSelectedComment && updateCommentsToHighlight([initialSelectedComment.id]);
   }, [initialSelectedComment]);

   useEffect(() => {
      if (!searchText.length && cachedNewComments.length) {
         setCommentsToHighlight(comments, cachedNewComments);
         updateCommentsState({comments: cachedNewComments});
         setCachedNewComments([]);
      }
   }, [searchText]);

   const mapCommentsToTextArray = (commentsToMap: IComment[]): string[] => {
      if (!commentsToMap.length) {
         return [];
      }
      return commentsToMap.flatMap(c => [c.text, ...c.replies.map(cr => cr.text)]);
   };

   const setCommentsToHighlight = (prevComments: IComment[], newComments: IComment[]) => {
      const prevCommentIds = prevComments.map(c => c.id);
      const newCommentsToHighlight = newComments?.map(c => c.id).filter(id => !prevCommentIds.includes(id));
      updateCommentsToHighlight(newCommentsToHighlight);
   };

   const setSearchText = (text: string) => {
      updateSearchText(text);

      if (!text.length) {
         void AriaHelper.readAlert(t('conversations.commentSearchTextClearedAlert'));
         comments && updateCommentsState({comments: comments.map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}))});
      }
   };

   const onPerformCommentSearch = () => {
      setCommentsToHighlight(comments, []);
      if (searchText.length) {
         const commentsToDisplay = comments?.filter(comment =>
            comment.text.toLowerCase().includes(searchText.toLowerCase()) ||
            comment.user.DisplayName.toLowerCase().includes(searchText.toLowerCase()) ||
            comment.replies.filter((reply: IComment) => reply.text.toLowerCase().includes(searchText.toLowerCase()) || reply.user.DisplayName.toLowerCase().includes(searchText.toLowerCase())).length !== 0
         );
         void AriaHelper.readAlert(t('conversations.commentSearchTextAlert', {count: commentsToDisplay.length}));
         comments && updateCommentsState({
            comments: comments.map(c => Object.assign(c, {
               isFiltered: !(commentsToDisplay.includes(c)),
               replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: !reply.text.toLowerCase().includes(searchText.toLowerCase()) && !reply.user.DisplayName.toLowerCase().includes(searchText.toLowerCase())}))
            }))
         });
      } else {
         comments && updateCommentsState({comments: comments.map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}))});
      }
   };

   const submitNewComment = async (commentText: string) => {
      const mediaInterface = await importMediaInterface();
      setAddCommentLoadingState({isWorking: true, hasError: false});
      LocalStorageWrapper.removeItem(Constants.localStorageKeys.unsavedCommentText.replace('{videoReviewId}', VideoReviewId));

      try {
         if (conversations) {
            const conversationsCopy = [...conversations];
            const conversationToUpdate = conversationsCopy.find(conversation => conversation.VideoReviewId === VideoReviewId);
            const updateCommenterCount = comments.findIndex(comment => comment.authorId === config.user.TechSmithId) === -1;
            const updatedConversation = Object.assign({}, conversationToUpdate, {CommentCount: conversationToUpdate.CommentCount + 1, CommenterCount: updateCommenterCount ? conversationToUpdate.CommenterCount + 1 : conversationToUpdate.CommenterCount});
            conversationsCopy.splice(conversationsCopy.findIndex(oldConversation => oldConversation.VideoReviewId === VideoReviewId), 1, updatedConversation);
            updateConversationsState({conversations: conversationsCopy});
         }

         const commentsCopy = [...comments];
         const newComment = Object.assign({},
            await conversationApi.addComment(VideoReviewId,
               {
                  text: commentText.trim(),
                  annotation: (annotationMode === AnnotationModes.edit && canvasRef?.getObjects().length) ? JSON.stringify(canvasRef?.toDatalessJSON()) : '',
                  isResolved: false,
                  mediaTimeInMilliseconds: mediaInterface.currentTime * 1000,
                  originatingService: config.featureSwitches.IsScreencast ? ServiceNames.screencast : ServiceNames.knowmia
               }
            ),
            {
               authorId: config.user.TechSmithId,
               user: {
                  ProfileImageUrl: config.user.AvatarImageUrl,
                  DisplayName: config.user.DisplayName,
                  Id: config.user.TechSmithId
               }
            }
         );
         commentsCopy.push(newComment);
         updateSearchText('');
         setCommentsToHighlight(comments, [newComment]);
         const sortedComments = !searchText.length ?
            sortCommentsByTime(commentsCopy) :
            sortCommentsByTime(commentsCopy).map(c => Object.assign(c, {isFiltered: undefined, replies: c.replies.map((reply: IComment) => Object.assign({}, reply, {isFiltered: undefined}))}));
         updateCommentsState({comments: sortedComments});
         updateAnnotationManagerState({annotationMode: newComment.annotation ? AnnotationModes.display : AnnotationModes.none});
         updateSelectedConversationState({SelectedComment: newComment});
         void AriaHelper.readAlert(t('conversations.commentSubmitted'));
         setAddCommentLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to submit new comment', e);
         setAddCommentLoadingState({isWorking: false, hasError: true});
      }
   };

   const clearSelfIdentifiedUser = () => {
      try {
         setAddCommentLoadingState({isWorking: true, hasError: false});
         canvasRef?.clear();
         updateAnnotationManagerState({annotationMode: AnnotationModes.none});
         selfIdentifyService.forgetSelfIdentifiedUser();
         setShowSelfIdentifyAlertModal(false);
         setAddCommentLoadingState({isWorking: false, hasError: false});
      } catch (e) {
         logging.error('Failed to clear self identified user', e);
         setAddCommentLoadingState({isWorking: false, hasError: true});
      }
   };

   const updateConversation = (e: React.ChangeEvent<HTMLSelectElement>): void => {
      if (!e.target.value) {
         updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
         return;
      }

      const newSelectedConversation = conversations.find(conversation => conversation.VideoReviewId === e.target.value);
      updateSelectedConversationState({VideoReviewId: newSelectedConversation.VideoReviewId, Title: newSelectedConversation.Title, ConversationHash: newSelectedConversation.ConversationHash, SelectedComment: null});
   };

   const createConversation = async () => {
      setConversationLoadingState({isWorking: true, hasError: false});
      try {
         const newConversation = await conversationApi.createConversation(Hash, Title);
         conversations.push(newConversation);
         updateConversationsState({conversations: conversations});
         updateSelectedConversationState({Title: newConversation.Title, VideoReviewId: newConversation.VideoReviewId, ConversationHash: newConversation.ConversationHash, SelectedComment: null});
         setConversationLoadingState({isWorking: false, hasError: false});
      } catch {
         setConversationLoadingState({isWorking: false, hasError: true});
      }
   };

   const getEmptyStateBody = () => {
      if (conversationLoadingState.hasError) {
         return (
            <EmptyStateContainer>
               <ConversationEmptyState />
               <EmptyState
                  title={t('conversations.emptyStateText.conversationNotFound')}
                  primaryButtonText={t('general.close')}
                  primaryButtonType={TscButtonType.secondary}
                  onPrimaryClick={() => {
                     conversationLoadingState.hasError && updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});
                     updateMediaTabsState({mediaTabsPanelOpen: false});
                  }}
               />
            </EmptyStateContainer>
         );
      } else if (!conversations?.length && (config.watchConfig.isOwnerAdminOrTeamEditor)) {
         return (
            <EmptyStateContainer>
               <ConversationEmptyState />
               <EmptyState
                  title={t('conversations.emptyStateText.noConversationsExist')}
                  text={t('conversations.emptyStateSubText.noConversationsExist')}

                  primaryButtonText={config.featureSwitches.UseSingleConversation ? t('conversations.enableConversation') : t('conversations.addConversationTitle')}
                  primaryButtonType={TscButtonType.secondary}
                  onPrimaryClick={() => config.featureSwitches.UseSingleConversation ? createConversation() : setShowCreateConversationModal(true)}
               />
            </EmptyStateContainer>
         );
      } else if (!VideoReviewId) {
         return (
            <EmptyStateContainer>
               <ConversationEmptyState />
               <EmptyState
                  title={t('conversations.emptyStateText.noConversationSelected')}
                  text={t('conversations.emptyStateSubText.noConversationSelected')}
               />
            </EmptyStateContainer>
         );
      }

      const noCommentBody = (
         <>
            <NoCommentEmptyStateMessageText>
               {t('conversations.emptyStateText.noComments.flavorText1')}
               <br />
               {t('conversations.emptyStateText.noComments.flavorText2')}
               <AuthorNameSpan>
                  {Author}
               </AuthorNameSpan>
            </NoCommentEmptyStateMessageText>
         </>
      );

      return (
         <EmptyStateContainer>
            <ConversationEmptyState />
            <TitleContainer theme={theme}>
               {searchText.length ? t('conversations.emptyStateText.noFilterResults') : t('conversations.emptyStateText.noComments.headerLine')}
            </TitleContainer>
            {noCommentBody}
         </EmptyStateContainer>
      );
   };

   const showEmptyState = !comments?.length || !comments.filter(c => !c.isFiltered).length;
   const emptyState: React.JSX.Element = (
      <EmptyStateWrapper>
         {getEmptyStateBody()}
      </EmptyStateWrapper>
   );

   const conversationOptions = [{value: '', label: t('conversations.emptyState.selectPrompt')}];
   if (!config.featureSwitches.UseSingleConversation) {
      conversations?.forEach(conversation => {
         conversationOptions.push({value: conversation.VideoReviewId, label: conversation.Title});
      });
   }

   const toggleResolvedComments = () => {
      setIsHidingResolvedComments(!isHidingResolvedComments);
   };

   const commentComponents = useMemo(() => {
      if (!comments?.length) {
         return null;
      }

      return comments.map((comment: IComment, index: number) => (
         <Comment
            key={index}
            comment={comment}
            isReply={false}
            shouldShowUpdate={commentsToHighlight?.indexOf(comment.id) > -1}
            setCommentsToHighlight={setCommentsToHighlight}
            isHidingResolvedComments={isHidingResolvedComments}
         />
      ));
   }, [comments, commentsToHighlight, setCommentsToHighlight, isHidingResolvedComments]);

   return (
      <>
         <ConversationContainer
            data-test-id="conversation-container"
            theme={theme}
            isLti={config.environmentData.IsLti}
            tabIndex={-1}
            smartPlayerDisplayMode={smartPlayerDisplayMode}
         >
            <ConversationContainerHeaderWrapper theme={theme}>
               {!config.featureSwitches.UseSingleConversation && !!conversations?.length && !config.environmentData.IsLti && config.watchConfig.isOwnerOrAdmin && (
                  <ConversationSelectWrapper
                     theme={theme}
                     borderNeeded={!!VideoReviewId}
                     data-test-id="conversation-select"
                  >
                     <SelectInput
                        label={t('conversations.selectConversationLabel')}
                        value={VideoReviewId}
                        onChange={updateConversation}
                        options={conversationOptions}
                     />
                  </ConversationSelectWrapper>
               )}
               {!!conversations?.length && (
                  <CommentSectionHeader theme={theme}>
                     <CommentHeaderRight>
                        {VideoReviewId && (
                           <CommentFilter
                              searchText={searchText}
                              setSearchText={setSearchText}
                              executeSearch={onPerformCommentSearch}
                              isHidingResolvedComments={isHidingResolvedComments}
                              toggleResolvedComments={toggleResolvedComments}
                           />
                        )}
                     </CommentHeaderRight>
                  </CommentSectionHeader>
               )}
               {isSelfIdentified && !conversationLoadingState.hasError && (
                  <SelfIdentifiedUserWrapper theme={theme}>
                     {t('conversations.selfIdentified.commentingAs', {displayName: config.user.DisplayName})}
                     <ButtonStyledAsAnchor
                        onClick={() => setShowSelfIdentifyAlertModal(true)}
                        title={t('conversations.selfIdentified.changeUserToolTip')}
                     >
                        {' '}
                        {t('conversations.selfIdentified.changeUser')}
                     </ButtonStyledAsAnchor>
                  </SelfIdentifiedUserWrapper>
               )}
               {VideoReviewId && !conversationLoadingState.hasError && (
                  <CommentInput
                     hasAnnotation={!!canvasRef?.getObjects().length}
                     isCommentOwner={true}
                     isReply={false}
                     onSubmit={(commentText: string) => submitNewComment(commentText)}
                     loadingState={addCommentLoadingState}
                  />
               )}
            </ConversationContainerHeaderWrapper>
            {/* eslint-disable-next-line eqeqeq */}
            {conversationLoadingState.isWorking && !conversationLoadingState.hasError || (conversations == null && config.watchConfig.isOwnerOrAdmin) ? (

               <SpinnerWrapper>
                  <Spinner />
               </SpinnerWrapper>
            ) : (
               <CommentSection
                  id="comment-section"
                  className="comment-section"
                  showEmptyState={showEmptyState}
               >
                  {showEmptyState ? emptyState : commentComponents}
                  <BottomSpacer />
               </CommentSection>
            )}
         </ConversationContainer>
         {showCreateConversationModal && <CreateConversationModal onClose={() => setShowCreateConversationModal(false)} />}
         {showSelfIdentifyModal && (
            <SelfIdentifyModal
               showTosMessage={true}
               showSignInMessage={true}
               isEmbedded={false}
               onClose={() => updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: false})}
            />
         )}
         {showSelfIdentifyAlertModal && (
            <AlertModalPortal
               title={t('conversations.selfIdentified.changeUser')}
               confirmContent={t('conversations.selfIdentified.changeUser')}
               closeContent={t('general.cancel')}
               dismissContent={t('general.cancel')}
               onClose={() => setShowSelfIdentifyAlertModal(false)}
               onDismiss={() => setShowSelfIdentifyAlertModal(false)}
               onConfirm={clearSelfIdentifiedUser}
               dismissButtonType={TscButtonType.secondary}
               confirmButtonType={TscButtonType.destructive}
            >
               {t('conversations.selfIdentified.changeUserWarningText', {displayName: config.user.DisplayName})}
            </AlertModalPortal>
         )}
      </>
   );
};

export interface IConversationProps {
   conversationLoadingState: IWorkerState;
   setConversationLoadingState: (updatedState: IWorkerState) => void;
   initialSelectedComment: IComment;
   smartPlayerDisplayMode: string;
}

export interface IStateMappedProps {
   canvasRef: any;
   annotationMode: AnnotationModes;
   comments: IComment[];
   VideoReviewId: string;
   conversations: IConversationData[];
   ConversationHash: string;
   Hash: string;
   Title: string;
   Author: string;
   showSelfIdentifyModal: boolean;
   IsReplacingMedia: boolean;
}

const mapStatesToProps = (
   {conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode}: IConversationProps,
   {canvasRef}: Partial<IStateMappedProps>,
   {annotationMode}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {VideoReviewId, ConversationHash}: Partial<IStateMappedProps>,
   {conversations}: Partial<IStateMappedProps>,
   {Hash, Title, Author}: Partial<IStateMappedProps>,
   {showSelfIdentifyModal}: Partial<IStateMappedProps>,
   {IsReplacingMedia}: Partial<IStateMappedProps>
): IStateMappedProps & IConversationProps => ({canvasRef, annotationMode, comments, VideoReviewId, ConversationHash, conversations, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, Hash, Title, Author, showSelfIdentifyModal, IsReplacingMedia});

export default withMemoizedContexts(mapStatesToProps, canvasStore, annotationManagerStore, commentsStore, selectedConversationStore, conversationsStore, mediaStore, selfIdentifiedUserStore, mediaOperationsStore)(ConversationBase);
